import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../services/pricingConfigService';

const inp: React.CSSProperties = {
  width: 90, padding: '5px 8px', border: '1px solid #cdd6e0', borderRadius: 6, fontSize: 13,
};
const txt: React.CSSProperties = { ...inp, width: 150 };
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '.04em', color: '#6b7684', borderBottom: '1px solid #e3e9ef', fontWeight: 600,
};
const td: React.CSSProperties = { padding: '8px 10px', borderBottom: '1px solid #eef2f6', fontSize: 13 };

export const PricingConfig: React.FC = () => {
  // Action price list (ActionPointMasters): what the usage queue charges from.
  const [actions, setActions] = useState<api.ActionPoint[]>([]);
  /** Guids of rows edited on screen but not saved yet. */
  const [dirty, setDirty] = useState<Set<string>>(new Set());
  const [savingGuid, setSavingGuid] = useState<string | null>(null);
  const [actionSearch, setActionSearch] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [bundles, setBundles] = useState<api.TopUpBundle[]>([]);
  const [policy, setPolicy] = useState<api.TrialPolicy | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const flash = (m: string) => { setErr(''); setMsg(m); setTimeout(() => setMsg(''), 2500); };
  const fail = (e: any) => setErr(String(e?.message || e));

  const loadAll = () => {
    api.getActionPoints().then(rows => { setActions(rows); setDirty(new Set()); }).catch(fail);
    api.getBundles().then(bs => setBundles([...bs].sort((a, b) => a.sortOrder - b.sortOrder))).catch(fail);
    api.getTrialPolicy().then(setPolicy).catch(fail);
  };
  useEffect(loadAll, []);

  // ---- action price list (ActionPointMasters) ----
  const PRICE_NOTE = 'New actions are charged at this price within 10 minutes.';

  const visibleActions = useMemo(() => {
    const q = actionSearch.trim().toLowerCase();
    return actions
      .filter(r => !activeOnly || r.isActive || dirty.has(r.actionPointMasterGuid))
      .filter(r => !q
        || r.actionName.toLowerCase().includes(q)
        || (r.moduleName ?? '').toLowerCase().includes(q)
        || (r.functionName ?? '').toLowerCase().includes(q))
      .sort((a, b) => (a.moduleName ?? '').localeCompare(b.moduleName ?? '') || a.actionName.localeCompare(b.actionName));
  }, [actions, actionSearch, activeOnly, dirty]);

  const activeCount = actions.filter(r => r.isActive).length;
  const pricedCount = actions.filter(r => r.isActive && Number(r.points) > 0).length;

  const updAp = (guid: string, patch: Partial<api.ActionPoint>) => {
    setActions(rows => rows.map(r => (r.actionPointMasterGuid === guid ? { ...r, ...patch } : r)));
    setDirty(d => new Set(d).add(guid));
  };

  const saveAp = (r: api.ActionPoint) => {
    if (Number(r.points) < 0 || isNaN(Number(r.points))) { fail('Credits must be zero or more.'); return; }
    setSavingGuid(r.actionPointMasterGuid);
    api.updateActionPoint(r.actionPointMasterGuid, {
      actionName: r.actionName,
      points: Number(r.points),
      moduleName: r.moduleName?.trim() || null,
      functionName: r.functionName?.trim() || null,
      isActive: r.isActive,
    })
      .then(saved => {
        setActions(rows => rows.map(x => (x.actionPointMasterGuid === saved.actionPointMasterGuid ? saved : x)));
        setDirty(d => { const n = new Set(d); n.delete(saved.actionPointMasterGuid); return n; });
        return api.clearUsageCache().catch(() => null);
      })
      .then(() => flash(`Saved "${r.actionName}". ${PRICE_NOTE}`))
      .catch(fail)
      .finally(() => setSavingGuid(null));
  };

  const [nAp, setNAp] = useState({ moduleName: '', actionName: '', functionName: '', points: 1 });
  const addAp = () => {
    const name = nAp.actionName.trim();
    if (!name) { fail('Enter the action name the service sends, e.g. CreateShipment.'); return; }
    if (Number(nAp.points) < 0 || isNaN(Number(nAp.points))) { fail('Credits must be zero or more.'); return; }
    api.createActionPoint({
      actionName: name,
      points: Number(nAp.points),
      moduleName: nAp.moduleName.trim() || null,
      functionName: nAp.functionName.trim() || null,
      isActive: true,
    })
      .then(created => {
        setActions(rows => [...rows, created]);
        setNAp({ moduleName: '', actionName: '', functionName: '', points: 1 });
        return api.clearUsageCache().catch(() => null);
      })
      .then(() => flash(`Added "${name}". ${PRICE_NOTE}`))
      .catch(fail);
  };

  // ---- bundles ----
  const updB = (id: string, patch: Partial<api.TopUpBundle>) =>
    setBundles(bs => bs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const saveB = (r: api.TopUpBundle) =>
    api.saveBundle({ id: r.id, credits: Number(r.credits), priceUsd: Number(r.priceUsd), popular: r.popular, discountPct: r.discountPct ?? null, sortOrder: r.sortOrder, active: r.active })
      .then(() => flash('Bundle saved')).catch(fail);

  const [nB, setNB] = useState({ credits: 100, priceUsd: 2 });
  const addB = () =>
    api.saveBundle({ credits: Number(nB.credits), priceUsd: Number(nB.priceUsd), popular: false, sortOrder: bundles.length + 1, active: true })
      .then(() => { setNB({ credits: 100, priceUsd: 2 }); loadAll(); flash('Bundle added'); }).catch(fail);

  // ---- trial + tax ----
  const savePolicy = () => policy && api.saveTrialPolicy(policy).then(() => flash('Settings saved')).catch(fail);
  const setP = (patch: Partial<api.TrialPolicy>) => setPolicy(p => (p ? { ...p, ...patch } : p));

  return (
    <div className="ov-page">
      <header className="ov-header">
        <div>
          <h1 className="ov-header__title">Pricing &amp; Credits</h1>
          <p className="ov-header__sub">Configure what each action costs, the buy-credit bundles, and trial &amp; tax — stored in the database.</p>
        </div>
      </header>

      {(msg || err) && (
        <div style={{ margin: '0 0 14px', padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
          background: err ? '#fdecea' : '#e7f6ec', color: err ? '#c0392b' : '#1e7e46', border: `1px solid ${err ? '#f5c6cb' : '#bfe6cd'}` }}>
          {err || msg}
        </div>
      )}

      {/* Action credit costs: ActionPointMasters, the price list the usage queue charges from */}
      <section className="ov-section">
        <div className="ov-section__head"><h2 className="ov-section__title">Action credit costs</h2></div>
        <p style={{ color: '#6b7684', fontSize: 13, margin: '0 0 10px' }}>
          Credits deducted each time a tenant performs an action. The action name must match what the
          Logipulse service sends (for example CreateShipment). An action with 0 credits, or one that is not
          active, is recorded but not charged. Price changes apply to new actions within 10 minutes; actions
          already charged keep their price.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', margin: '0 0 10px' }}>
          <input style={{ ...txt, width: 260 }} placeholder="Search action, module or name"
                 value={actionSearch} onChange={e => setActionSearch(e.target.value)} />
          <label style={{ fontSize: 13, color: '#4a5563', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} />
            Active only
          </label>
          <span style={{ fontSize: 12, color: '#6b7684' }}>
            {activeCount} active, {pricedCount} charged (credits above 0), {actions.length} in total
          </span>
          {dirty.size > 0 && (
            <span style={{ fontSize: 12, color: '#b26a00' }}>{dirty.size} unsaved change{dirty.size > 1 ? 's' : ''}</span>
          )}
        </div>

        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
            <thead><tr>
              <th style={th}>Module</th><th style={th}>Action name</th><th style={th}>Display name</th>
              <th style={th}>Credits</th><th style={th}>Active</th><th style={th}>Last changed</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {visibleActions.map(r => {
                const guid = r.actionPointMasterGuid;
                const isDirty = dirty.has(guid);
                const changed = r.modifiedDateUtc || r.createdDateUtc;
                return (
                  <tr key={guid} style={r.isActive ? undefined : { opacity: 0.6 }}>
                    <td style={td}><input style={inp} value={r.moduleName ?? ''} onChange={e => updAp(guid, { moduleName: e.target.value })} /></td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }} title="Sent by the service; not editable">{r.actionName}</td>
                    <td style={td}><input style={txt} value={r.functionName ?? ''} onChange={e => updAp(guid, { functionName: e.target.value })} /></td>
                    <td style={td}><input style={inp} type="number" step="0.1" min="0" value={r.points} onChange={e => updAp(guid, { points: Number(e.target.value) })} /></td>
                    <td style={td}><input type="checkbox" checked={r.isActive} onChange={e => updAp(guid, { isActive: e.target.checked })} /></td>
                    <td style={{ ...td, fontSize: 12, color: '#6b7684' }}>
                      {changed ? new Date(changed.endsWith('Z') ? changed : changed + 'Z').toLocaleDateString() : ''}
                      {(r.modifiedBy || r.createdBy) ? <div>{r.modifiedBy || r.createdBy}</div> : null}
                    </td>
                    <td style={td}>
                      <button className="btn-small btn-small--primary" disabled={!isDirty || savingGuid === guid}
                              onClick={() => saveAp(r)}>
                        {savingGuid === guid ? 'Saving...' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {visibleActions.length === 0 && (
                <tr><td style={{ ...td, color: '#6b7684' }} colSpan={7}>No actions match.</td></tr>
              )}
              <tr>
                <td style={td}><input style={inp} placeholder="Module" value={nAp.moduleName} onChange={e => setNAp({ ...nAp, moduleName: e.target.value })} /></td>
                <td style={td}><input style={{ ...inp, width: 150 }} placeholder="e.g. CreateShipment" value={nAp.actionName} onChange={e => setNAp({ ...nAp, actionName: e.target.value })} /></td>
                <td style={td}><input style={txt} placeholder="Display name" value={nAp.functionName} onChange={e => setNAp({ ...nAp, functionName: e.target.value })} /></td>
                <td style={td}><input style={inp} type="number" step="0.1" min="0" value={nAp.points} onChange={e => setNAp({ ...nAp, points: Number(e.target.value) })} /></td>
                <td style={td} /><td style={td} />
                <td style={td}><button className="btn-small btn-small--ghost" onClick={addAp}>+ Add</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Top-up bundles */}
      <section className="ov-section">
        <div className="ov-section__head"><h2 className="ov-section__title">Top-up bundles</h2></div>
        <p style={{ color: '#6b7684', fontSize: 13, margin: '0 0 10px' }}>The buy-credit options shown to tenants — credits granted and the price charged.</p>
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead><tr>
              <th style={th}>Credits</th><th style={th}>Price (USD)</th><th style={th}>Per 1,000</th>
              <th style={th}>Popular</th><th style={th}>Active</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {bundles.map(r => (
                <tr key={r.id}>
                  <td style={td}><input style={inp} type="number" min="1" value={r.credits} onChange={e => updB(r.id, { credits: Number(e.target.value) })} /></td>
                  <td style={td}>$<input style={{ ...inp, width: 80 }} type="number" step="0.01" min="0" value={r.priceUsd} onChange={e => updB(r.id, { priceUsd: Number(e.target.value) })} /></td>
                  <td style={{ ...td, color: '#6b7684' }}>${r.credits > 0 ? Math.round((r.priceUsd / r.credits) * 1000) : 0}</td>
                  <td style={td}><input type="checkbox" checked={r.popular} onChange={e => updB(r.id, { popular: e.target.checked })} /></td>
                  <td style={td}><input type="checkbox" checked={r.active} onChange={e => updB(r.id, { active: e.target.checked })} /></td>
                  <td style={td}><button className="btn-small btn-small--primary" onClick={() => saveB(r)}>Save</button></td>
                </tr>
              ))}
              <tr>
                <td style={td}><input style={inp} type="number" min="1" value={nB.credits} onChange={e => setNB({ ...nB, credits: Number(e.target.value) })} /></td>
                <td style={td}>$<input style={{ ...inp, width: 80 }} type="number" step="0.01" min="0" value={nB.priceUsd} onChange={e => setNB({ ...nB, priceUsd: Number(e.target.value) })} /></td>
                <td style={{ ...td, color: '#6b7684' }}>${nB.credits > 0 ? Math.round((nB.priceUsd / nB.credits) * 1000) : 0}</td>
                <td style={td} /><td style={td} />
                <td style={td}><button className="btn-small btn-small--ghost" onClick={addB}>+ Add</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Trial + tax */}
      <section className="ov-section">
        <div className="ov-section__head"><h2 className="ov-section__title">Trial &amp; tax</h2></div>
        {policy ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 22, alignItems: 'flex-end', background: '#fff', border: '1px solid #e3e9ef', borderRadius: 10, padding: 18 }}>
            <label style={{ fontSize: 12, color: '#6b7684' }}>Free credits (on signup)<br />
              <input style={{ ...inp, marginTop: 5 }} type="number" step="1" min="0" value={policy.freeCredit} onChange={e => setP({ freeCredit: Number(e.target.value) })} /></label>
            <label style={{ fontSize: 12, color: '#6b7684' }}>Data trial (days)<br />
              <input style={{ ...inp, marginTop: 5 }} type="number" step="1" min="0" value={policy.dataTrialDays} onChange={e => setP({ dataTrialDays: Number(e.target.value) })} /></label>
            <label style={{ fontSize: 12, color: '#6b7684' }}>Tax (%)<br />
              <input style={{ ...inp, marginTop: 5 }} type="number" step="0.1" min="0" value={policy.taxPercent} onChange={e => setP({ taxPercent: Number(e.target.value) })} /></label>
            <button className="btn-small btn-small--primary" onClick={savePolicy}>Save settings</button>
          </div>
        ) : <p style={{ color: '#6b7684', fontSize: 13 }}>Loading…</p>}
      </section>

      {/* Blocked trial visitors USED TO BE HERE. Moved to its own page — see
          pages/TrialVisitors.tsx and the 'Trial visitors' nav item.

          It only ever listed the BLOCKED visitors, which is the wrong starting
          point: an agent taking a call does not yet know whether the person is
          blocked. The new page lists everybody, adds country and city, and sorts
          by status. It kept the same Save and Reopen actions.

          Removed rather than left alongside: two screens editing the same rows
          means a Save in one leaves the other showing values that are no longer
          true. */}
    </div>
  );
};
